// Library Methods
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import TableSkeleton from "src/components/containers/shared/TableSkeleton/TableSkeleton";
import CustomIconButton from "src/components/shared/buttons/CustomIconButton.tsx/CustomIconButton";
import ExpandableSearchbar from "src/components/shared/inputs/ExpandableSearchbar/ExpandableSearchbar";
import TableHeader from "src/components/shared/tables/TableHeader/TableHeader";
import ClinicPatientCard from "./ClinicPatientCard/ClinicPatientCard";
import CustomPagination from "src/components/shared/pagination/CustomPagination/CustomPagination";
import ErrorText from "src/components/shared/text/ErrorText/ErrorText";
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import AddEditPatientModal from "./AddEditPatientModal/AddEditPatientModal";
import { PlusIcon } from "src/assets/icons";

// Types
import { AddEditPatientType, PatientType } from "src/services/patients/types";
import { OrderType } from "src/types/sharedTypes";

// Utils
import { multiKeyCompare } from "src/utils/tables/tableHelper";
import {
  calculateYearDifference,
  getLocalDate,
} from "src/utils/date/dateHelper";
import {
  getItemsToRender,
  getPageCount,
} from "src/utils/pagination/paginationHelper";

// Styles
import { CustomIconButtonDark } from "src/styles/mui/muiStylesHelper";

// Hooks
import useDebounce from "src/hooks/input/useDebounce";
import { useAddPatient } from "src/services/patients/hooks/useAddPatient";
import { useUpdatePatient } from "src/services/patients/hooks/useUpdatePatient";
import { useDeletePatients } from "src/services/patients/hooks/useDeletePatients";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";

const MAX_NUM_OF_ROWS = 6;

type ClinicPatientsType = {
  patients: Array<PatientType> | undefined;
  selectedPatient: PatientType | undefined;
  setSelectedPatient: React.Dispatch<
    React.SetStateAction<PatientType | undefined>
  >;
  isLoading: boolean;
  isError: boolean;
};

const ClinicPatients = ({
  patients,
  selectedPatient,
  setSelectedPatient,
  isLoading,
  isError,
}: ClinicPatientsType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const theme = useTheme();
  // In dark mode the page background sits close to pure black, so the
  // pulsing highlight's "off" state is lightened a step to stay visible.
  const pulseOffBackground = theme.palette.mode === "dark" ? "#4D5063" : "#000";

  const location = useLocation();
  const [hasAutoSelectedPatient, setHasAutoSelectedPatient] = useState(false);

  const {
    mutate: addMutate,
    data: addedPatient,
    isPending: isAddPending,
    isSuccess: isAddSuccess,
  } = useAddPatient(clinicInfo?.id);

  const {
    mutate: editMutate,
    data: editedPatient,
    isPending: isEditPending,
    isSuccess: isEditSuccess,
  } = useUpdatePatient(clinicInfo?.id);

  const { mutate: deleteMutate, isPending: isDeletePending } =
    useDeletePatients(clinicInfo?.id);

  const [patientToEdit, setPatientToEdit] = useState<PatientType | undefined>();
  const [patientToDelete, setPatientToDelete] = useState<
    PatientType | undefined
  >();
  const [openPatientModal, setOpenPatientModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const orderByPatientName: Array<keyof PatientType> = [
    "firstName",
    "lastName",
  ];
  const orderByNextExamDate: Array<keyof PatientType> = [
    "nextScheduledExamDate",
  ];
  const orderBySex: Array<keyof PatientType> = ["sex"];
  const orderByLanguage: Array<keyof PatientType> = ["language"];
  const orderByBirthdate: Array<keyof PatientType> = ["dateOfBirth"];

  const [page, setPage] = useState(1);
  const [order, setOrder] = useState<OrderType>("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedTerm = useDebounce(searchTerm);

  const [orderByOptions, setOrderByOptions] = useState<
    Array<keyof PatientType>
  >([...orderByPatientName]);

  const headCells = [
    {
      id: "name",
      label: t("Name"),
      gridSize: 3,
      orderUsing: [...orderByPatientName],
    },
    {
      id: "age",
      label: t("Age"),
      gridSize: 1.5,
      orderUsing: [...orderByBirthdate, ...orderByPatientName],
    },
    {
      id: "sex",
      label: t("Sex"),
      gridSize: 1.5,
      orderUsing: [...orderBySex, ...orderByPatientName],
    },
    {
      id: "language",
      label: t("Language"),
      gridSize: 2.3,
      orderUsing: [...orderByLanguage, ...orderByPatientName],
    },
    {
      id: "scheduled",
      label: t("Scheduled"),
      gridSize: 2.7,
      orderUsing: [...orderByNextExamDate, ...orderByPatientName],
    },
    {
      id: "extra-cell",
      gridSize: 1,
      orderUsing: [],
    },
  ];

  const handleRequestSort = useCallback(
    (
      _: React.MouseEvent<HTMLButtonElement>,
      property: Array<keyof PatientType>
    ) => {
      const isAsc =
        orderByOptions.length &&
        orderByOptions[0] === property[0] &&
        order === "asc";

      setOrder(isAsc ? "desc" : "asc");
      setOrderByOptions(property);
    },
    [order, orderByOptions]
  );

  // Memoize the searchable patients list for performance reasons
  // TODO: this will be removed when pagination is implemented
  const searchablePatients = useMemo(
    () =>
      (patients ?? []).map((patient) => ({
        ...patient,
        searchableName: `${patient?.firstName} ${patient?.lastName}`
          .toLowerCase()
          .trim(),
        searchableFormattedName:
          `${patient?.lastName?.slice(0, 2)},${patient?.firstName?.slice(0, 2)}`
            .toLowerCase()
            .trim(),
        searchableAge: (
          calculateYearDifference(
            patient?.dateOfBirth,
            clinicInfo?.timezone
          )?.toString() ?? ""
        ).toLowerCase(),
        searchableHealthCareId: (patient.healthCareId ?? "")
          .toString()
          .toLowerCase(),
        searchableScheduledDate: (
          getLocalDate(patient?.nextScheduledExamDate, clinicInfo?.timezone) ??
          ""
        ).toLowerCase(),
      })),
    [patients, clinicInfo?.timezone]
  );

  const filteredRows = useMemo(() => {
    const debouncedLowerCase = debouncedTerm?.toLowerCase() ?? "";

    const filterArray = searchablePatients.filter(
      (patient) =>
        patient.searchableName.includes(debouncedLowerCase) ||
        patient.searchableFormattedName.includes(debouncedLowerCase) ||
        patient.searchableAge.includes(debouncedLowerCase) ||
        patient.searchableHealthCareId.includes(debouncedLowerCase) ||
        patient.searchableScheduledDate.includes(debouncedLowerCase)
    );

    return multiKeyCompare<PatientType>(
      filterArray,
      order === "asc",
      orderByOptions
    );
  }, [order, orderByOptions, searchablePatients, debouncedTerm]);

  const isLoadingPatients = useMemo(
    () => isLoading || isAddPending || isEditPending || isDeletePending,
    [isLoading, isAddPending, isEditPending, isDeletePending]
  );

  const hasPatients = useMemo(
    () => !isLoadingPatients && !isError && patients && patients?.length > 0,
    [isLoadingPatients, isError, patients]
  );

  const hasNoSearchResults = useMemo(
    () => debouncedTerm?.length > 0 && !filteredRows?.length,
    [debouncedTerm, filteredRows]
  );

  const hasNoPatients = useMemo(
    () => !isLoadingPatients && !isError && !patients?.length,
    [isLoadingPatients, isError, patients]
  );

  const rowsToRender = useMemo(() => {
    return getItemsToRender(filteredRows, page, MAX_NUM_OF_ROWS);
  }, [filteredRows, page]);

  // Pagination change
  const onPaginationChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
    },
    [setPage]
  );

  // Searchbar change
  const onSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (page > 1) setPage(1);
    setSearchTerm(event?.target?.value);
  };

  // Clear searchbar
  const clearSearchFilter = () => {
    setSearchTerm("");
  };

  // Handle clicking the exam card
  const handleCardClick = useCallback(
    (_: React.MouseEvent<HTMLDivElement, MouseEvent>, patient: PatientType) => {
      setSelectedPatient(patient);
    },
    [setSelectedPatient]
  );

  const addPatient = useCallback(
    (patientData: AddEditPatientType) => {
      addMutate(patientData, { onSuccess: () => setOpenPatientModal(false) });
    },
    [addMutate]
  );

  const editPatient = useCallback(
    (newPatientData: AddEditPatientType) => {
      editMutate(
        {
          patientId: patientToEdit?.id ?? "",
          patientData: newPatientData,
        },
        { onSuccess: () => setOpenPatientModal(false) }
      );
    },
    [patientToEdit, editMutate]
  );

  const deletePatient = useCallback(() => {
    if (!patientToDelete) return;

    deleteMutate([patientToDelete.id], {
      onSuccess: () => {
        if (selectedPatient?.id === patientToDelete.id) {
          setSelectedPatient(undefined);
        }
        setPatientToDelete(undefined);
        setOpenDeleteModal(false);
      },
    });
  }, [
    patientToDelete,
    selectedPatient?.id,
    deleteMutate,
    setSelectedPatient,
    setPatientToDelete,
  ]);

  const selectPatientAndPage = useCallback(
    (patientToSelect: PatientType) => {
      // Find the index of the existing patient in the filteredRows array
      const selectedIndex = filteredRows?.findIndex(
        (row) => row.id === patientToSelect?.id
      );
      if (selectedIndex < 0) return;

      // Calculate the page number
      const selectedPage =
        selectedIndex >= 0
          ? Math.ceil((selectedIndex + 1) / MAX_NUM_OF_ROWS)
          : 1;

      setPage(selectedPage);
      setSelectedPatient({ ...patientToSelect });
    },
    [filteredRows, setSelectedPatient]
  );

  // When viewing an existing patient from the modal, show the patient's page and select the patient
  const navigateToExistingPatient = useCallback(
    (existingPatient: PatientType) => {
      setOpenPatientModal(false);
      selectPatientAndPage(existingPatient);
    },
    [selectPatientAndPage]
  );

  // After adding a new patient, show the patient's page and select the patient
  useEffect(() => {
    if (addedPatient && isAddSuccess) {
      selectPatientAndPage(addedPatient);
    }
  }, [addedPatient, isAddSuccess, selectPatientAndPage]);

  // After updating a patient, show the patient's page and select the patient
  useEffect(() => {
    if (editedPatient && isEditSuccess) {
      selectPatientAndPage(editedPatient);
    }
  }, [editedPatient, isEditSuccess, selectPatientAndPage]);

  const handlePatientToEdit = useCallback(
    (inputPatient: PatientType) => {
      setPatientToEdit(inputPatient);
      setPatientToDelete(undefined);
      setSelectedPatient(undefined);
      setOpenPatientModal(true);
    },
    [setSelectedPatient]
  );

  // if returning from a reports page or clickable dashboard card,
  // set the selected patient to be the patient from the report/card
  useEffect(() => {
    if (
      !hasAutoSelectedPatient &&
      filteredRows?.length > 0 &&
      ((location?.state?.stateId && location?.state?.fromReport) ||
        location?.state?.patient_id ||
        Boolean(localStorage.getItem("patient_id")))
    ) {
      const patientId =
        location?.state?.stateId ??
        location?.state?.patient_id ??
        localStorage.getItem("patient_id");

      const patientIndex = filteredRows.findIndex(
        (row) => row.id === patientId
      );

      if (patientIndex < MAX_NUM_OF_ROWS) {
        setPage(1);
      } else {
        const selectedPatientsPage = Math.ceil(patientIndex / MAX_NUM_OF_ROWS);
        setPage(selectedPatientsPage);
      }

      localStorage.removeItem("patient_id");
      setSelectedPatient(filteredRows[patientIndex]);
      setHasAutoSelectedPatient(true);
    }
  }, [
    location?.state?.stateId,
    location?.state?.fromReport,
    location?.state?.patient_id,
    filteredRows,
    hasAutoSelectedPatient,
    setSelectedPatient,
  ]);

  // Display the selected patient's page when resetting the search term
  useEffect(() => {
    const selectedIndex = filteredRows.findIndex(
      (row) => row.id === selectedPatient?.id
    );
    const selectedPatientPage =
      selectedIndex >= 0 ? Math.ceil((selectedIndex + 1) / MAX_NUM_OF_ROWS) : 1;
    setPage(selectedPatientPage);
  }, [filteredRows, selectedPatient?.id, setPage]);

  return (
    <Box display="flex" width="100%" flexDirection="column">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="12px"
        mb="20px"
      >
        <Typography
          height="40px"
          alignContent="center"
          fontSize="16px"
          fontWeight={600}
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
          maxWidth="100%"
        >
          {t("Patients")}
        </Typography>
        <Box display="flex" gap="8px">
          {(hasPatients || isLoadingPatients) && (
            <ExpandableSearchbar
              id="clinic-patients-searchbar"
              isLoading={isLoadingPatients}
              searchTerm={searchTerm}
              onChange={onSearchChange}
              onClear={clearSearchFilter}
              searchTooltip={t("Search Patients")}
              openByDefault={true}
            />
          )}
          <CustomIconButton
            isLoading={isLoadingPatients}
            tooltipTitle={t("Add Patient")}
            icon={<PlusIcon />}
            customSx={
              patients?.length === 0
                ? {
                    "@keyframes color-change-icon": {
                      "0%": {
                        backgroundColor: pulseOffBackground,
                      },
                      "50%": {
                        backgroundColor: "#E2772E",
                      },
                      "100%": {
                        backgroundColor: pulseOffBackground,
                      },
                    },
                    animation: false ? "none" : "color-change-icon 2s infinite",
                  }
                : undefined
            }
            onClick={() => {
              setPatientToEdit(undefined);
              setPatientToDelete(undefined);
              setSelectedPatient(undefined);
              setOpenPatientModal(true);
            }}
          />
        </Box>
      </Box>

      {isLoadingPatients && <TableSkeleton numOfRows={MAX_NUM_OF_ROWS} />}

      {hasPatients && !hasNoSearchResults && (
        <Box display="flex" flexDirection="column" gap="12px">
          <TableHeader<PatientType>
            headCells={headCells}
            order={order}
            orderBy={orderByOptions?.[0] ?? "firstName"}
            onRequestSort={handleRequestSort}
            rowCount={filteredRows.length}
            numSelected={0}
          />
          {rowsToRender?.map((patient) => (
            <ClinicPatientCard
              key={patient.id}
              patient={patient}
              isSelectedForViewing={selectedPatient?.id === patient?.id}
              handleCardClick={handleCardClick}
              handlePatientToEdit={handlePatientToEdit}
              handleDeletePatient={(patient) => {
                setPatientToDelete(patient);
                setOpenDeleteModal(true);
              }}
            />
          ))}
          <Box mt="12px">
            <CustomPagination
              page={page}
              count={getPageCount(filteredRows?.length, MAX_NUM_OF_ROWS)}
              onChange={onPaginationChange}
            />
          </Box>
        </Box>
      )}

      <AddEditPatientModal
        title={patientToEdit ? t("Edit Patient") : t("Add Patient")}
        open={openPatientModal}
        onClose={() => setOpenPatientModal(false)}
        onConfirm={patientToEdit ? editPatient : addPatient}
        allPatients={patients}
        patientToEdit={patientToEdit}
        navigateToExistingPatient={navigateToExistingPatient}
      />

      <SharedModal
        open={openDeleteModal}
        title={t("Delete Patient")}
        modalDescription={t(
          "Are you sure you want to delete {{ patientName }}?",
          {
            patientName: `${patientToDelete?.firstName} ${patientToDelete?.lastName}`,
          }
        )}
        confirmButtonText={t("Delete")}
        confirmButtonVariant="error"
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={deletePatient}
      />

      {/* Error messages */}
      {hasNoPatients && (
        <ErrorText
          header={t("No Patients Found.")}
          message={
            <Typography maxWidth="400px">
              {t("Click the")}{" "}
              <IconButton
                sx={{
                  ...CustomIconButtonDark(theme),
                  width: `${UI_SIZES.iconButtonSize}px`,
                  height: `${UI_SIZES.iconButtonSize}px`,
                  pointerEvents: "none",
                }}
              >
                <PlusIcon />
              </IconButton>{" "}
              {t("button above to create a patient.")}
            </Typography>
          }
        />
      )}
      {!hasNoPatients && hasNoSearchResults && (
        <ErrorText
          header={t("No Patients Found.")}
          message={t("Change your search term to see more results.")}
        />
      )}
      {isError && (
        <ErrorText
          header={t("Something Went Wrong.")}
          message={t(
            "Your patients could not be fetched. Please refresh the page or try again later."
          )}
        />
      )}
    </Box>
  );
};

export default ClinicPatients;
